import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { toast } from "react-toastify";
import { Save, X, Building2 } from "lucide-react";

import { useFirmAppStore } from "../../store/firmAppStore";
import { useFirmStore } from "../../store/firmStore";
import { useSelectDialog } from "../../hooks/useSelectDialog";
import { CustomSelectInput } from "../../components/CustomSelectInput";
import { FirmApp } from "../../types/firmApp";
import { Firm } from "../../types/firm";
import { IconTextButton } from "../../components/ButtonComponents";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  app: FirmApp | null;
}

export const FirmAppDialog: React.FC<Props> = ({ isOpen, onClose, app }) => {
  const { createItem, updateItem } = useFirmAppStore();
  const { data: firmData, fetchDataDynamic: fetchFirms, loading: firmLoading } = useFirmStore();
  const firmSelectDialog = useSelectDialog<Firm>();

  const [formData, setFormData] = useState({ 
    firmId: 0, 
    firmName: "", 
    appName: "", 
    appIdentifier: "", 
    isActive: true 
  });

  useEffect(() => {
    if (app) {
      setFormData({ 
        firmId: app.firmId, 
        firmName: (app as any).firm?.name || "", 
        appName: app.appName, 
        appIdentifier: app.appIdentifier, 
        isActive: app.isActive 
      });
    } else {
      setFormData({ firmId: 0, firmName: "", appName: "", appIdentifier: "", isActive: true });
    }
  }, [app, isOpen]);

  async function openFirmSelection() {
    await fetchFirms({ pageIndex: 1, pageSize: 10 });
    const selected = await firmSelectDialog.open({
      title: "Firma Seç",
      data: firmData,
      columns: [{ field: "id", header: "ID" }, { field: "name", header: "Firma Adı" }],
      loading: firmLoading,
      onFilter: async (params) => {
        const res = await fetchFirms(params);
        return { data: res.items ?? [], total: res.total ?? 0 };
      },
    });

    if (selected) {
      setFormData(prev => ({ ...prev, firmId: selected.id, firmName: selected.name }));
    }
  }

 const save = async () => {
  if (!formData.firmId) {
    toast.warn("Lütfen bir firma seçin.");
    return;
  }

  // 🌟 ÇÖZÜM: firmName'i payload'dan ayıkla
  const { firmName, ...payload } = formData;

  try {
    if (app) {
      await updateItem(app.id, payload);
    } else {
      await createItem(payload);
    }
    toast.success("İşlem başarılı");
    onClose();
  } catch (e: any) {
    // 422 hatasının detayını konsolda görmek için:
    console.error("API Hatası:", e.response?.data);
    toast.error("Kaydetme hatası: " + (e.response?.data?.message || "Bilinmeyen hata"));
  }
};

  return (
    <>
      <Dialog header={app ? "Düzenle" : "Yeni Uygulama"} visible={isOpen} onHide={onClose} className="w-96">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <CustomSelectInput
              label="Firma *"
              value={formData.firmName || ""}
              placeholder="Firma Seç..."
              icon={Building2}
              onClick={openFirmSelection}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Uygulama Adı</label>
            <InputText value={formData.appName} onChange={(e) => setFormData({...formData, appName: e.target.value})} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Identifier</label>
            <InputText value={formData.appIdentifier} onChange={(e) => setFormData({...formData, appIdentifier: e.target.value})} />
          </div>
          <div className="flex items-center gap-2">
            <InputSwitch checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.value!})} />
            <label>Aktif</label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
             <IconTextButton icon={X} onClick={onClose} className="btn-cancel">İptal</IconTextButton>
             <IconTextButton icon={Save} onClick={save} className="btn-new">Kaydet</IconTextButton>
          </div>
        </div>
      </Dialog>
      {firmSelectDialog.modal}
    </>
  );
};